import React, { useState } from 'react';
import TeslaRobot from '../components/TeslaRobot';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  
  // FAQ data
  const faqs: FAQItem[] = [
    // General Questions
    {
      question: "What is AiDA Digital Workplace?",
      answer: "AiDA Digital Workplace is a platform that provides AI-powered digital employees with specialized skills to help businesses automate tasks, improve productivity, and reduce costs. Our AI employees can handle a wide range of tasks from content creation to data analysis, project management, and more.",
      category: "general"
    },
    {
      question: "How do AI employees differ from chatbots?",
      answer: "Unlike traditional chatbots that follow predefined scripts, our AI employees use advanced large language models to understand context, generate human-like responses, and perform complex tasks. They can learn from interactions, adapt to your organization's specific needs, and handle sophisticated workflows that go far beyond simple question-answering.",
      category: "general"
    },
    {
      question: "Can AI employees replace human workers?",
      answer: "AI employees are designed to augment human capabilities, not replace them. They excel at handling repetitive, time-consuming tasks, allowing your human employees to focus on higher-value work that requires creativity, emotional intelligence, and strategic thinking. The most effective approach is a collaborative model where AI and humans work together.",
      category: "general"
    },
    
    // Technical Questions
    {
      question: "What technology powers your AI employees?",
      answer: "Our AI employees are powered by state-of-the-art large language models (LLMs) that have been fine-tuned for specific domains and skills. We use a combination of proprietary models and industry-leading technologies, enhanced with reinforcement learning from human feedback (RLHF) to continuously improve performance.",
      category: "technical"
    },
    {
      question: "How secure is my data when using AI employees?",
      answer: "We take data security extremely seriously. All communications with our AI employees are encrypted, and we implement enterprise-grade security measures to protect your information. We offer customizable data retention policies, and you maintain full control over what data is shared with the AI employees. For enterprise customers, we also offer private deployment options.",
      category: "technical"
    },
    {
      question: "Can I integrate AI employees with my existing systems?",
      answer: "Yes, our AI employees can integrate with your existing tools and systems through our comprehensive API. This allows them to access data from your CRM, project management tools, document storage, and other business applications. Our team can help you set up custom integrations for your specific needs.",
      category: "technical"
    },
    
    // Usage Questions
    {
      question: "How do I communicate with AI employees?",
      answer: "You can communicate with AI employees through natural language in our chat interface. Simply type your instructions or questions as you would to a human colleague. For more complex tasks, you can provide detailed requirements, examples, or reference materials to guide the AI employee's work.",
      category: "usage"
    },
    {
      question: "What kinds of tasks can AI employees perform?",
      answer: "AI employees can perform a wide range of tasks depending on their specialization. This includes content creation (blog posts, emails, social media), data analysis and reporting, project management, customer support, research, code generation, document summarization, translation, and much more. Each AI employee has specific skills listed in their profile.",
      category: "usage"
    },
    {
      question: "How do I get the best results from AI employees?",
      answer: "To get the best results, provide clear and specific instructions. Include context about your goals, audience, and preferences. When appropriate, provide examples of the type of output you're looking for. If the initial result isn't quite right, provide feedback and ask for revisions. The more you interact with an AI employee, the better it will understand your preferences and requirements.",
      category: "usage"
    },
    
    // Pricing Questions
    {
      question: "How does your pricing work?",
      answer: "Our pricing is based on a per-seat, per-month model, with the number of AI employees you need. Each seat costs $20 per month and gives a user access to your organization's AI employees. We offer volume discounts for larger organizations, and we have a free tier with limited functionality for you to try the platform.",
      category: "pricing"
    },
    {
      question: "Is there a limit to how much I can use the AI employees?",
      answer: "Paid plans include unlimited usage of your AI employees. The free tier has a limit of 5 hours per month. There are no hidden fees or charges based on usage volume, making it easy to budget for your AI workforce.",
      category: "pricing"
    },
    {
      question: "Do you offer discounts for non-profits or educational institutions?",
      answer: "Yes, we offer special pricing for non-profits, educational institutions, and startups. Please contact our sales team for more information about our discount programs.",
      category: "pricing"
    },
    
    // Implementation Questions
    {
      question: "How long does it take to implement AI employees?",
      answer: "You can start using our standard AI employees immediately after signing up. For custom AI employees tailored to your specific business processes, implementation typically takes 2-4 weeks, depending on the complexity of your requirements and the level of customization needed.",
      category: "implementation"
    },
    {
      question: "Do you provide training for our team?",
      answer: "Yes, all paid plans include access to our comprehensive documentation and video tutorials. Business and Enterprise plans also include personalized onboarding sessions and training for your team. We're committed to ensuring your team can effectively collaborate with your AI employees.",
      category: "implementation"
    },
    {
      question: "Can AI employees be customized for our specific business needs?",
      answer: "Absolutely. While our standard AI employees are ready to use out of the box, we also offer customization options. For Business and Enterprise customers, we can develop custom AI employees trained on your company's specific data, terminology, and processes to provide even more tailored assistance.",
      category: "implementation"
    }
  ];
  
  // Filter FAQs based on active category
  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);
  
  // Toggle FAQ item expansion
  const toggleItem = (index: number) => {
    if (expandedItems.includes(index)) {
      setExpandedItems(expandedItems.filter(item => item !== index));
    } else {
      setExpandedItems([...expandedItems, index]);
    }
  };
  
  // Categories for filter
  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'general', label: 'General' },
    { id: 'technical', label: 'Technical' },
    { id: 'usage', label: 'Usage' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'implementation', label: 'Implementation' }
  ];
  
  return (
    <div className="faq-page">
      <div className="faq-header">
        <div className="faq-title-container">
          <h1 className="faq-title">
            Frequently Asked <span className="text-gradient">Questions</span>
          </h1>
          <p className="faq-subtitle">
            Find answers to common questions about our AI employees and digital workplace
          </p>
        </div>
        <div className="faq-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>
      
      <div className="faq-content">
        <div className="faq-categories">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-button ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.label}
            </button>
          ))}
        </div>
        
        <div className="faq-list">
          {filteredFAQs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${expandedItems.includes(index) ? 'expanded' : ''}`}
            >
              <div className="faq-question" onClick={() => toggleItem(index)}>
                <h3>{faq.question}</h3>
                <div className="faq-toggle">
                  {expandedItems.includes(index) ? '−' : '+'}
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="faq-contact">
        <h2>Still have questions?</h2>
        <p>Our team is here to help you get the most out of your AI employees.</p>
        <div className="contact-buttons">
          <a href="/contact" className="contact-button primary">Contact Support</a>
          <a href="/demo" className="contact-button secondary">Try the Demo</a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
